import React from 'react';
import './UserLeadsReport.css';

// ============================================================================
// UserReportView — mobile-PDF-style sectioned report body.
// Pure presentational: pass `detail` = response.data from GET /api/reports/users/:id
// Used by the per-user report page AND inline in the Leads Report detail area.
// ============================================================================

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const prettyDate = (val) => {
  if (!val) return '';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return String(val);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

const ACTIVITY_LABELS = {
  status_update: 'Status Updates',
  call: 'Calls',
  note: 'Notes',
  followup: 'Follow-ups',
  whatsapp: 'WhatsApp',
  email: 'Emails',
  sms: 'SMS',
  assignment: 'Assignments',
  meeting: 'Meetings',
  site_visit: 'Site Visits',
};
const labelize = (k) =>
  ACTIVITY_LABELS[k] || String(k).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const Section = ({ title, children }) => (
  <div className="mrpt-section">
    <h3 className="mrpt-section__title">{title}</h3>
    {children}
  </div>
);
const Card = ({ value, label }) => (
  <div className="mrpt-card">
    <span className="mrpt-card__value">{value}</span>
    <span className="mrpt-card__label">{label}</span>
  </div>
);
const ListRow = ({ label, value, color }) => (
  <div className="mrpt-row">
    <span className="mrpt-row__label">
      {color && <span className="mrpt-row__dot" style={{ background: color }} />}
      {label}
    </span>
    <span className="mrpt-row__value">{value}</span>
  </div>
);

const UserReportView = ({ detail }) => {
  if (!detail) return null;

  const leads = detail.leads || {};
  const calls = detail.calls || {};
  const fu = detail.followups || {};
  const att = detail.attendance || {};
  const act = detail.activities || {};
  const login = detail.login || {};
  const breakdown = leads.statusBreakdown || [];
  const movement = leads.statusMovement || [];
  const actEntries = Object.entries(act.breakdown || {});

  return (
    <>
      <Section title="Lead Overview">
        <div className="mrpt-cards">
          <Card value={leads.assigned || 0} label="Assigned" />
          <Card value={leads.created || 0} label="Created" />
          <Card value={leads.newAssignments || 0} label="New Assigned" />
        </div>
      </Section>

      {breakdown.length > 0 && (
        <Section title="Lead Status Breakdown">
          <div className="mrpt-list">
            {breakdown.map((s, i) => (
              <ListRow key={i} label={s.status_name || 'Unassigned'} value={s.count || 0} color={s.status_color || '#94a3b8'} />
            ))}
          </div>
        </Section>
      )}

      {movement.length > 0 && (
        <Section title="Status Movement">
          <div className="mrpt-list">
            {movement.map((m, i) => (
              <ListRow key={i} label={`${m.from || 'New / Unset'} → ${m.to || 'Unknown'}`} value={m.count || 0} />
            ))}
          </div>
        </Section>
      )}

      <Section title="Call Summary">
        <div className="mrpt-cards">
          <Card value={calls.total || 0} label="Total" />
          <Card value={calls.outgoing || 0} label="Outgoing" />
          <Card value={calls.incoming || 0} label="Incoming" />
          <Card value={calls.missed || 0} label="Missed" />
        </div>
      </Section>

      <Section title="Follow-ups">
        <div className="mrpt-cards">
          <Card value={fu.total || 0} label="Total" />
          <Card value={fu.completed || 0} label="Completed" />
          <Card value={fu.pending || 0} label="Pending" />
          <Card value={fu.cancelled || 0} label="Cancelled" />
          <Card value={fu.overdue || 0} label="Overdue" />
        </div>
      </Section>

      <Section title="Attendance">
        <div className="mrpt-cards">
          <Card value={att.presentDays || 0} label="Present" />
          <Card value={att.absentDays || 0} label="Absent" />
          <Card value={att.halfdayDays || 0} label="Half Day" />
          <Card value={att.leaveDays || 0} label="Leave" />
        </div>
      </Section>

      {actEntries.length > 0 && (
        <Section title={`Activities (${act.total || 0})`}>
          <div className="mrpt-list">
            {actEntries.map(([k, v]) => (
              <ListRow key={k} label={labelize(k)} value={v} />
            ))}
          </div>
        </Section>
      )}

      <Section title="Login & Device">
        <div className="mrpt-list">
          <ListRow label="Total Logins" value={login.totalLogins || 0} />
          <ListRow label="Device" value={login.lastDevice || '—'} />
          <ListRow label="Login Location" value={login.lastLoginAddress || 'Not allowed'} />
        </div>
      </Section>

      <div className="mrpt-footer">Generated on {prettyDate(new Date())}</div>
    </>
  );
};

export default UserReportView;
